import React, { useState, useEffect } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';

const BUILD_NUMBER = 'BUILD_20260324_v3.8';

const CodeAITab = ({ selectedProject, selectedModel, isActive, selectedRepo, repoLoading }) => {
  // Bug Hunt State
  const [bugHuntStep, setBugHuntStep] = useState('idle'); // 'idle', 'selecting', 'hunting', 'results'
  const [jiraBugTickets, setJiraBugTickets] = useState([]);
  const [loadingJiraBugs, setLoadingJiraBugs] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [bugSearch, setBugSearch] = useState('');
  const [bugTotalCount, setBugTotalCount] = useState(0);
  const [bugCurrentPage, setBugCurrentPage] = useState(1);
  const [bugPageSize, setBugPageSize] = useState(50); // Will load ALL bugs
  const [bugTotalPages, setBugTotalPages] = useState(1);
  const [bugHasNextPage, setBugHasNextPage] = useState(false);
  const [bugHasPrevPage, setBugHasPrevPage] = useState(false);
  const [bugDaysFilter, setBugDaysFilter] = useState(7); // Default 7 days
  const [autoLoadedProject, setAutoLoadedProject] = useState(null); // Track which project was auto-loaded
  
  // Bug Hunt Results
  const [bugHuntResults, setBugHuntResults] = useState(null);
  const [isHunting, setIsHunting] = useState(false);
  const [huntError, setHuntError] = useState(null);

  // Styles
  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      fontSize: '14px'
    },
    section: {
      marginBottom: '2rem',
      padding: '20px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9'
    },
    heading: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '15px',
      color: '#000'
    },
    subheading: {
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#333'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
      fontSize: '14px',
      color: '#333'
    },
    input: {
      width: '100%',
      padding: '10px',
      marginBottom: '10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      fontSize: '14px'
    },
    select: {
      width: '100%',
      padding: '10px',
      marginBottom: '10px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '14px',
      backgroundColor: '#fff',
      color: '#000'
    },
    button: {
      padding: '12px 20px',
      marginRight: '10px',
      marginBottom: '10px',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px',
      fontFamily: 'inherit',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#667eea',
    },
    successButton: {
      backgroundColor: '#28a745',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
    },
    disabledButton: {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed',
      opacity: 0.6
    },
    error: {
      padding: '12px',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      borderRadius: '4px',
      marginBottom: '15px',
      border: '1px solid #f5c6cb'
    },
    success: {
      padding: '12px',
      backgroundColor: '#d4edda',
      color: '#155724',
      borderRadius: '4px',
      marginBottom: '15px',
      border: '1px solid #c3e6cb'
    },
    card: {
      padding: '15px',
      backgroundColor: '#fff',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      marginBottom: '15px'
    },
    bugCard: {
      padding: '15px',
      backgroundColor: '#fff',
      border: '2px solid #dee2e6',
      borderRadius: '6px',
      marginBottom: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: '#667eea',
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)'
      }
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '15px',
      marginTop: '15px'
    },
    badge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 'bold',
      marginRight: '5px'
    },
    timelineItem: {
      padding: '15px',
      backgroundColor: '#f5f5f5',
      border: '1px solid #ddd',
      borderRadius: '4px',
      marginBottom: '10px',
      borderLeft: '4px solid #667eea'
    }
  };

  // Auto-load bugs when selectedProject changes or component becomes active
  useEffect(() => {
    if (isActive && selectedProject && autoLoadedProject !== selectedProject) {
      console.log('[CODE-AI] ✨ Auto-loading bugs on tab activation for project:', selectedProject);
      console.log('[CODE-AI] Previous autoLoadedProject:', autoLoadedProject);
      setAutoLoadedProject(selectedProject); // Mark this project as auto-loaded
      // Immediately fetch on next tick to avoid state update batching issues
      fetchJiraBugs(1);
    }
  }, [isActive, selectedProject, autoLoadedProject]);

  // Fetch JIRA bug tickets for the selected project
  const fetchJiraBugs = async (pageNum = 1) => {
    if (!selectedProject) {
      setHuntError('Please select a JIRA project first');
      return;
    }

    setLoadingJiraBugs(true);
    setHuntError(null);

    try {
      console.log('[CODE-AI] 🔵 START: Fetching JIRA bugs for project:', selectedProject, 'page:', pageNum, 'days:', bugDaysFilter);
      
      const apiUrl = getApiBase();
      const fullUrl = `${apiUrl}/api/jira/bugs?project=${selectedProject}&page=${pageNum}&limit=${bugPageSize}&days=${bugDaysFilter}`;
      console.log('[CODE-AI] API URL:', fullUrl);
      
      const token = await authService.getAccessToken();
      console.log('[CODE-AI] Token obtained:', token ? 'YES' : 'NO');
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[CODE-AI] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CODE-AI] ❌ API Error response:', errorText);
        throw new Error(`Failed to fetch bugs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[CODE-AI] ✅ API Response received:', { 
        totalBugs: data.total, 
        bugsReturned: data.bugs?.length,
        page: data.page,
        totalPages: data.totalPages,
        hasNext: data.hasNextPage,
        hasPrev: data.hasPrevPage,
        daysFilter: data.daysFilter,
        fullResponse: data
      });
      
      console.log('[CODE-AI] 📝 Setting state...');
      console.log('[CODE-AI] About to setBugTotalCount to:', data.total);
      setJiraBugTickets(data.bugs || []);
      setBugTotalCount(data.total);
      setBugCurrentPage(data.page || 1);
      setBugTotalPages(data.totalPages || 1);
      setBugHasNextPage(data.hasNextPage || false);
      setBugHasPrevPage(data.hasPrevPage || false);
      setBugHuntStep('selecting');
      
      console.log(`[CODE-AI] ✅ COMPLETE - State updated - Total: ${data.total}, Page: ${data.page}/${data.totalPages}`);
    } catch (err) {
      console.error('[CODE-AI] ❌ ERROR:', err);
      console.error('[CODE-AI] Error stack:', err.stack);
      setHuntError(`Error fetching bugs: ${err.message}`);
    } finally {
      setLoadingJiraBugs(false);
    }
  };

  // Helper function to fetch with specific days value (for dropdown onChange)
  const fetchJiraBugsWithDays = async (pageNum = 1, daysValue = bugDaysFilter) => {
    if (!selectedProject) {
      setHuntError('Please select a JIRA project first');
      setLoadingJiraBugs(false);
      return;
    }

    try {
      console.log('[CODE-AI] 🔵 START: Fetching JIRA bugs with days filter:', daysValue);
      
      const apiUrl = getApiBase();
      const fullUrl = `${apiUrl}/api/jira/bugs?project=${selectedProject}&page=${pageNum}&limit=${bugPageSize}&days=${daysValue}`;
      
      const token = await authService.getAccessToken();
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CODE-AI] ❌ API Error response:', errorText);
        throw new Error(`Failed to fetch bugs: ${response.status}`);
      }

      const data = await response.json();
      console.log('[CODE-AI] ✅ API Response received:', { 
        totalBugs: data.total, 
        bugsReturned: data.bugs?.length
      });
      
      setJiraBugTickets(data.bugs || []);
      setBugTotalCount(data.total || 0);
      setBugCurrentPage(data.page || 1);
      setBugTotalPages(data.totalPages || 1);
      setBugHasNextPage(data.hasNextPage || false);
      setBugHasPrevPage(data.hasPrevPage || false);
      setBugHuntStep('selecting');
      setHuntError(null);
    } catch (err) {
      console.error('[CODE-AI] ❌ ERROR:', err);
      setHuntError(`Error fetching bugs: ${err.message}`);
    } finally {
      setLoadingJiraBugs(false);
    }
  };

  // Hunt for bug origin
  const startBugHunt = async () => {
    if (!selectedBug) {
      setHuntError('Please select a bug ticket');
      return;
    }

    if (!selectedRepo) {
      setHuntError('Please select a GitHub repository');
      return;
    }

    setIsHunting(true);
    setHuntError(null);

    try {
      const [owner, repo] = selectedRepo.split('/');
      
      console.log('[CODE-AI] Starting bug hunt for', selectedBug.key, 'in', selectedRepo);

      const apiUrl = getApiBase();
      const response = await fetch(
        `${apiUrl}/api/github/repos/${owner}/${repo}/find-bug-branch`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await authService.getAccessToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bugKey: selectedBug.key,
            affectedFiles: selectedBug.affectedFiles || [],
            bugDescription: selectedBug.summary,
            selectedModel: selectedModel || 'gemini-2.5-flash'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Bug hunt failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[CODE-AI] Bug hunt results:', data);
      
      setBugHuntResults(data.data || data);
      setBugHuntStep('results');
    } catch (err) {
      console.error('[CODE-AI] Error during bug hunt:', err);
      setHuntError(`Bug hunt error: ${err.message}`);
    } finally {
      setIsHunting(false);
    }
  };

  // Initialize - check if component is active
  useEffect(() => {
    if (!isActive) {
      // Reset state when switching away
      setSelectedBug(null);
      setBugHuntResults(null);
      setHuntError(null);
    }
  }, [isActive]);

  // Filtered bugs based on search
  const filteredBugs = jiraBugTickets.filter(bug =>
    bug.key.toLowerCase().includes(bugSearch.toLowerCase()) ||
    bug.summary.toLowerCase().includes(bugSearch.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <h2 style={{ ...styles.heading, marginBottom: '20px', color: '#000000' }}>
        🔎 Code AI - Bug Origin Hunter
      </h2>

      {/* Repository Status */}
      {selectedRepo ? (
        <div style={{ ...styles.section, backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '5px' }}>
            ✅ Repository Selected
          </div>
          <div style={{ fontSize: '13px', color: '#558b2f' }}>
            {selectedRepo}
          </div>
        </div>
      ) : (
        <div style={{ ...styles.section, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#e65100', marginBottom: '5px' }}>
            ⚠️ No Repository Selected
          </div>
          <div style={{ fontSize: '13px', color: '#d84315' }}>
            Please select a repository from the <strong>Pull Request Analysis</strong> tab first to use Bug Hunt.
          </div>
        </div>
      )}

      {/* Step 1: Select Bug Ticket */}
      <div style={styles.section}>
        <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px' }}>Build: {BUILD_NUMBER}</div>
        <h3 style={styles.subheading}>
          1️⃣ Select Bug Ticket from {selectedProject || 'Project'} ({bugTotalCount} total)
        </h3>

        {!selectedProject && (
          <div style={styles.error}>
            ⚠️ Please select a JIRA project in the main project dropdown first
          </div>
        )}

        {selectedProject && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={styles.label}>Filter by Date Range:</label>
              <select
                value={bugDaysFilter}
                onChange={(e) => {
                  const newDays = parseInt(e.target.value);
                  setBugDaysFilter(newDays);
                  setBugCurrentPage(1);
                  setBugSearch('');
                  // Trigger fetch automatically when filter changes
                  setTimeout(() => {
                    setLoadingJiraBugs(true);
                    fetchJiraBugsWithDays(1, newDays);
                  }, 0);
                }}
                disabled={loadingJiraBugs}
                style={styles.select}
              >
                <option value={7}>Last 7 days (Default)</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>

            {/* DEBUG: Show total loaded */}
            <div style={{ fontSize: '11px', color: '#999', marginTop: '8px', padding: '5px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>
              ✅ Loaded {jiraBugTickets.length} bugs | Total matching: {bugTotalCount}
            </div>

            {jiraBugTickets.length > 0 && (
              <>
                <label style={styles.label}>Search Bugs:</label>
                <input
                  type="text"
                  placeholder="Search by key or summary..."
                  value={bugSearch}
                  onChange={(e) => setBugSearch(e.target.value)}
                  style={styles.input}
                />

                <div style={{ marginTop: '15px', maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredBugs.map((bug) => (
                    <div
                      key={bug.key}
                      onClick={() => setSelectedBug(bug)}
                      style={{
                        ...styles.card,
                        backgroundColor: selectedBug?.key === bug.key ? '#e3f2fd' : '#fff',
                        borderColor: selectedBug?.key === bug.key ? '#667eea' : '#dee2e6',
                        borderWidth: selectedBug?.key === bug.key ? '2px' : '1px',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        <a 
                          href={bug.ticketUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#667eea', textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {bug.key}
                        </a>
                      </div>
                      <div style={{ fontSize: '13px', color: '#333', marginBottom: '5px' }}>
                        {bug.summary}
                      </div>
                      {bug.status && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Status: <strong>{bug.status}</strong>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {selectedBug && (
                  <div style={styles.success}>
                    ✅ Selected: {selectedBug.key} - {selectedBug.summary}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Step 2: Start Bug Hunt */}
      {selectedBug && (
        <div style={styles.section}>
          <h3 style={styles.subheading}>2️⃣ Hunt Bug Origin</h3>

          {selectedRepo && !repoLoading ? (
            <>
              <div style={styles.success}>
                ✅ Ready to hunt bug <strong>{selectedBug.key}</strong> in <strong>{selectedRepo}</strong>
              </div>

              <button
                onClick={startBugHunt}
                disabled={isHunting || repoLoading}
                style={{
                  ...styles.button,
                  backgroundColor: (isHunting || repoLoading) ? '#6c757d' : '#28a745',
                  cursor: (isHunting || repoLoading) ? 'not-allowed' : 'pointer',
                  opacity: (isHunting || repoLoading) ? 0.6 : 1
                }}
              >
                {isHunting ? '🔍 Hunting Bug Origin...' : '🚀 Start Bug Hunt'}
              </button>
            </>
          ) : repoLoading ? (
            <div style={styles.error}>
              ⏳ Loading repository... Please wait.
            </div>
          ) : (
            <div style={styles.error}>
              ⚠️ Please select a repository from the <strong>Pull Request Analysis</strong> tab to hunt for bugs.
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {huntError && (
        <div style={styles.error}>
          ❌ {huntError}
        </div>
      )}

      {/* Bug Hunt Results */}
      {bugHuntStep === 'results' && bugHuntResults && (
        <div style={styles.section}>
          <h3 style={styles.subheading}>
            🎯 Bug Hunt Results for {bugHuntResults.bugKey}
          </h3>

          {/* Summary Cards */}
          <div style={styles.grid}>
            <div style={{ ...styles.card, backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
              <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: 'bold' }}>Bug Origins Found</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2196f3', marginTop: '5px' }}>
                {bugHuntResults.bugOrigins?.length || 0}
              </div>
            </div>

            <div style={{ ...styles.card, backgroundColor: '#f3e5f5', borderLeft: '4px solid #9c27b0' }}>
              <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Affected Branches</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#9c27b0', marginTop: '5px' }}>
                {bugHuntResults.affectedBranches?.length || 0}
              </div>
            </div>

            <div style={{ ...styles.card, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
              <div style={{ fontSize: '12px', color: '#e65100', fontWeight: 'bold' }}>Repository</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ff9800', marginTop: '5px' }}>
                {bugHuntResults.repository}
              </div>
            </div>
          </div>

          {/* Bug Origins */}
          {bugHuntResults.bugOrigins && bugHuntResults.bugOrigins.length > 0 && (
            <div style={{ ...styles.card, marginTop: '20px' }}>
              <h4 style={{ ...styles.subheading, marginBottom: '15px' }}>
                🐛 Bug Origin Details
              </h4>

              {bugHuntResults.bugOrigins.slice(0, 5).map((origin, idx) => (
                <div key={idx} style={styles.timelineItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <strong>📄 File:</strong>{' '}
                      <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                        {origin.file}
                      </code>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Lines {origin.lineStart}-{origin.lineEnd}
                    </div>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <strong>🔗 Commit:</strong>{' '}
                    <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>
                      {origin.commitHash?.substring(0, 7)}
                    </code>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <strong>💬 Message:</strong> {origin.commitMessage}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    <span>👤 {origin.commitAuthor}</span>
                    <span>📅 {new Date(origin.commitDate).toLocaleDateString()}</span>
                  </div>

                  {origin.commitUrl && (
                    <div style={{ marginTop: '10px' }}>
                      <a
                        href={origin.commitUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#667eea',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        View on GitHub →
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Affected Branches */}
          {bugHuntResults.relatedBranches && bugHuntResults.relatedBranches.length > 0 && (
            <div style={{ ...styles.card, marginTop: '20px' }}>
              <h4 style={{ ...styles.subheading, marginBottom: '15px' }}>
                🌿 Branches Containing Bug
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {bugHuntResults.relatedBranches.map((branch, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      backgroundColor: branch.protected ? '#ffebee' : '#f5f5f5',
                      border: `1px solid ${branch.protected ? '#ef5350' : '#ddd'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong>🔀 {branch.name}</strong>
                      {branch.protected && (
                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
                          🔒 PROTECTED
                        </span>
                      )}
                    </div>
                    <code style={{ fontSize: '12px', backgroundColor: '#fff', padding: '2px 6px', borderRadius: '3px' }}>
                      {branch.commit?.substring(0, 7)}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          {bugHuntResults.timeline && bugHuntResults.timeline.length > 0 && (
            <div style={{ ...styles.card, marginTop: '20px' }}>
              <h4 style={{ ...styles.subheading, marginBottom: '15px' }}>
                📜 Bug Timeline
              </h4>

              {bugHuntResults.timeline.map((event, idx) => (
                <div key={idx} style={styles.timelineItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 'bold', color: '#667eea' }}>
                      #{event.sequence} - {event.type.replace(/_/g, ' ').toUpperCase()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {new Date(event.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#333', marginBottom: '5px' }}>
                    {event.message}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    By {event.author} in {event.file}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {bugHuntResults.recommendations && bugHuntResults.recommendations.length > 0 && (
            <div style={{ ...styles.card, marginTop: '20px', backgroundColor: '#f0f8ff' }}>
              <h4 style={{ ...styles.subheading, marginBottom: '15px', color: '#1976d2' }}>
                💡 AI Recommendations
              </h4>

              {bugHuntResults.recommendations.slice(0, 5).map((rec, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    border: '1px solid #64b5f6',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: '#1565c0'
                  }}
                >
                  {rec}
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setSelectedBug(null);
                setBugHuntResults(null);
              }}
              style={{
                ...styles.button,
                ...styles.primaryButton
              }}
            >
              🔄 Hunt Another Bug
            </button>

            <button
              onClick={() => {
                window.open(`https://github.com/${bugHuntResults.repository}`, '_blank');
              }}
              style={{
                ...styles.button,
                backgroundColor: '#6f42c1'
              }}
            >
              💻 Open Repository
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeAITab;
